import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Sparkles, Trash2, Plus } from "lucide-react";
import CapitalContributions from "./CapitalContributions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function MemberIntentions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [prayer, setPrayer] = useState("");
  const [composing, setComposing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/members/intentions`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch (e) {
      console.error("[Intentions] load:", e?.message);
      toast.error("Não foi possível carregar o Capital de Graças.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const compose = async () => {
    if (text.trim().length < 3) { toast.error("Escreva primeiro a sua intenção."); return; }
    setComposing(true);
    setPrayer("");
    try {
      const res = await fetch(`${API}/members/intentions/compose`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), lang: "pt" }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }
          if (payload.delta) setPrayer((p) => p + payload.delta);
          else if (payload.error) failed = true;
        }
      }
      if (failed) throw new Error("compose_failed");
    } catch (e) {
      console.error("[Intentions] compose:", e?.message);
      toast.error("O assistente não conseguiu compor a oração. Pode juntar a intenção sem oração.");
    }
    setComposing(false);
  };

  const submit = async () => {
    if (text.trim().length < 3) { toast.error("Escreva a sua intenção."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/members/intentions`,
        { text: text.trim(), prayer: prayer.trim() || null, anonymous },
        { withCredentials: true });
      toast.success("Intenção junta ao Capital de Graças da comunidade.");
      setText(""); setPrayer(""); setAnonymous(false);
      load();
    } catch (e) {
      console.error("[Intentions] create:", e?.message);
      toast.error("Não foi possível juntar a intenção.");
    }
    setSaving(false);
  };

  const pray = async (it) => {
    try {
      const r = await axios.post(`${API}/members/intentions/${it.id}/pray`, {}, { withCredentials: true });
      setItems((prev) => prev.map((x) => (x.id === it.id ? r.data : x)));
    } catch { toast.error("Erro."); }
  };

  const remove = async (it) => {
    try {
      await axios.delete(`${API}/members/intentions/${it.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Intenção removida.");
    } catch { toast.error("Erro a remover."); }
  };

  return (
    <div className="container-page py-10 lg:py-14" data-testid="member-intentions-page">
      <div className="flex items-center gap-4">
        <img src="/mta-avatar.jpg" alt="MTA" className="h-14 w-14 rounded-full border-2 border-[#D4AF37] object-cover" />
        <div>
          <div className="overline">Oração Partilhada</div>
          <h1 className="display-heading text-3xl sm:text-4xl text-[hsl(var(--primary))]">Capital de Graças.</h1>
        </div>
      </div>
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl leading-relaxed">
        Registe de forma confidencial o que <b>oferece</b>, <b>pede</b> e <b>agradece</b>, e imprima
        as suas contribuições para entregar no Santuário, onde são queimadas no dia 18 de cada mês.
        Mais abaixo, pode também partilhar intenções com a comunidade no mural de Oração Partilhada.
      </p>

      {/* Contribuições privadas (Ofereço / Peço / Agradeço) */}
      <CapitalContributions />

      {/* Nova intenção partilhada */}
      <div className="mt-12 max-w-2xl">
        <div className="overline">Oração Partilhada</div>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Confie uma intenção à Mãe Três Vezes Admirável e junte-a ao mural da comunidade.
          O assistente pode compor uma breve oração, e os outros sócios podem rezar por ela.
        </p>
      </div>
      <div className="mt-4 bg-white border border-[hsl(var(--border))] p-6 max-w-2xl" data-testid="intention-form">
        <label className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">A minha intenção</label>
        <textarea
          data-testid="intention-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={600}
          rows={3}
          placeholder="Ex.: Pela saúde do meu pai; por uma decisão importante na empresa…"
          className="mt-2 w-full border border-[hsl(var(--border))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--primary))] bg-[hsl(var(--background))]"
        />
        {prayer && (
          <div data-testid="intention-prayer-preview" className="mt-4 bg-[#FDFBF7] border-l-2 border-[#D4AF37] px-4 py-3 text-sm italic text-[#1A201F] leading-relaxed whitespace-pre-wrap">
            {prayer}
          </div>
        )}
        <label className="mt-4 flex items-center gap-2 text-sm text-[hsl(var(--foreground))] cursor-pointer">
          <input
            type="checkbox"
            data-testid="intention-anonymous-checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
            className="accent-[hsl(var(--primary))]"
          />
          Partilhar anonimamente
        </label>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            data-testid="intention-compose-btn"
            onClick={compose}
            disabled={composing || text.trim().length < 3}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[hsl(var(--primary))] hover:text-white transition-colors disabled:opacity-40"
          >
            <Sparkles className="h-4 w-4" /> {composing ? "A compor…" : prayer ? "Compor de novo" : "Compor oração"}
          </button>
          <button
            data-testid="intention-submit-btn"
            onClick={submit}
            disabled={saving || composing || text.trim().length < 3}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            <Plus className="h-4 w-4" /> Juntar ao Capital de Graças
          </button>
        </div>
      </div>

      {/* Mural */}
      <div className="mt-12">
        <div className="overline">Mural da comunidade</div>
        {loading ? (
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="intentions-empty">
            Ainda não há intenções partilhadas. Seja o primeiro a confiar uma intenção à MTA.
          </p>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {items.map((it) => (
              <div key={it.id} className="bg-white border border-[hsl(var(--border))] p-5 flex flex-col" data-testid={`intention-card-${it.id}`}>
                <p className="text-sm text-[hsl(var(--foreground))] leading-relaxed">{it.text}</p>
                {it.prayer && (
                  <p className="mt-3 text-sm italic text-[#0E4D45] leading-relaxed border-l-2 border-[#D4AF37] pl-3 whitespace-pre-wrap">
                    {it.prayer}
                  </p>
                )}
                <div className="mt-4 pt-3 border-t border-[hsl(var(--border))] flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                  <span data-testid={`intention-author-${it.id}`}>
                    {it.anonymous ? "Anónimo" : it.author} · {timeAgo(it.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    {it.is_mine && (
                      <button onClick={() => remove(it)} title="Remover" data-testid={`intention-delete-${it.id}`}
                        className="p-1.5 text-[hsl(var(--muted-foreground))] hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`intention-pray-${it.id}`}
                      onClick={() => pray(it)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] uppercase tracking-wider transition-colors ${
                        it.i_prayed
                          ? "border-[#D4AF37] bg-[#FDF6E3] text-[#8a6d1d]"
                          : "border-[hsl(var(--border))] hover:border-[#D4AF37] hover:text-[#8a6d1d]"
                      }`}
                    >
                      <HeartHandshake className="h-3.5 w-3.5" />
                      {it.i_prayed ? "Rezei por esta" : "Rezo por esta"} ({it.prayed_count})
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
